import { t } from '@/lib/i18n/messages.js';
import type { DashboardLayout } from '@quartup/reports-shared';
import type React from 'react';
import type {
  DashboardExecuteResponse,
  WidgetExecuteRequest,
  WidgetResult,
} from '../lib/dashboards-client.js';
import { WidgetTile } from './widget-tile.js';

interface DashboardGridProps {
  layout: DashboardLayout;
  executionResult: DashboardExecuteResponse | undefined;
  /** Set of widget IDs that are currently refreshing individually */
  loadingWidgets: ReadonlySet<string>;
  isExecuting: boolean;
  globalParams: Record<string, unknown>;
  widgetParamsMap: Record<string, Record<string, unknown>>;
  onRefreshWidget: (widgetId: string, req: WidgetExecuteRequest) => Promise<void> | void;
}

export function widgetGridStyle(
  position: { x: number; y: number; w: number; h: number },
  cols: number,
): React.CSSProperties {
  // B-1-overflow: clamp x so it never exceeds the last column, then clamp span so the
  // widget never overflows beyond the grid boundary (e.g. x=10, w=4, cols=12 → span 2).
  const clampedX = Math.max(0, Math.min(position.x, cols - 1));
  const clampedSpan = Math.min(position.w, cols - clampedX);
  return {
    gridColumn: `${clampedX + 1} / span ${clampedSpan}`,
    gridRow: `${position.y + 1} / span ${position.h}`,
  };
}

export function DashboardGrid({
  layout,
  executionResult,
  loadingWidgets,
  isExecuting,
  globalParams,
  widgetParamsMap,
  onRefreshWidget,
}: DashboardGridProps) {
  const { columns = 12, widgets } = layout;

  if (widgets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="text-sm text-[var(--color-qup-text-muted-dark)]">
          {t('dashboards.no_widgets')}
        </p>
      </div>
    );
  }

  function getResult(widgetId: string): WidgetResult | undefined {
    if (executionResult === undefined) return undefined;
    return executionResult.widgetResults[widgetId];
  }

  return (
    <div
      className="grid gap-4 p-4"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {widgets.map((widget) => {
        const widgetId = widget.id;
        const isWidgetLoading = isExecuting || loadingWidgets.has(widgetId);
        const result = getResult(widgetId);
        const title = widget.title?.es ?? widget.title?.en ?? widgetId;
        const widgetParams = widgetParamsMap[widgetId];

        return (
          <div
            key={widgetId}
            className="max-sm:col-span-full max-sm:row-auto"
            style={widgetGridStyle(widget.position, columns)}
          >
            <WidgetTile
              widgetId={widgetId}
              title={title}
              visualization={widget.visualization}
              {...(widget.viz_config !== undefined ? { vizConfig: widget.viz_config } : {})}
              result={result}
              isLoading={isWidgetLoading}
              onRefresh={(req) => onRefreshWidget(widgetId, req)}
              globalParams={globalParams}
              {...(widgetParams !== undefined ? { widgetParams } : {})}
            />
          </div>
        );
      })}
    </div>
  );
}
